import { initializeApp, FirebaseApp } from 'firebase/app'
import {
  getAuth,
  Auth,
  setPersistence,
  browserLocalPersistence,
  inMemoryPersistence,
  indexedDBLocalPersistence,
} from 'firebase/auth'

interface FirebaseConfig {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
}

let app: FirebaseApp | null = null
let authInstance: Auth | null = null

const isIOSOrSafari = (): boolean => {
  const ua = navigator.userAgent
  const isIOS = /iPad|iPhone|iPod/.test(ua)
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua)
  return isIOS || isSafari
}

const setAuthPersistence = async (auth: Auth): Promise<void> => {
  const persistenceOptions = isIOSOrSafari()
    ? [browserLocalPersistence, inMemoryPersistence]
    : [indexedDBLocalPersistence, browserLocalPersistence, inMemoryPersistence]

  for (const persistence of persistenceOptions) {
    try {
      await setPersistence(auth, persistence)
      return
    } catch (error) {
      console.warn(`Failed to set persistence to ${persistence.type}:`, error)
    }
  }
}

export const initializeFirebase = async (retryCount = 0): Promise<FirebaseApp> => {
  const MAX_RETRIES = 3
  const RETRY_DELAY = 1000

  if (!app) {
    try {
      const response = await fetch('/api/firebase-config')
      const config: FirebaseConfig = await response.json()
      app = initializeApp(config)
      authInstance = getAuth(app)
      await setAuthPersistence(authInstance)
    } catch (error) {
      if (retryCount < MAX_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)))
        return initializeFirebase(retryCount + 1)
      }
      throw error
    }
  }
  return app
}

export const getFirebaseAuth = (): Auth => {
  if (!authInstance) {
    throw new Error('Firebase must be initialized before getting auth')
  }
  return authInstance
}

export const auth = getFirebaseAuth
